import { z } from 'zod';

import { centsSchema } from '@nesso/common-contracts/cents';
import { defineCommand } from '@nesso/common-contracts/define';
import { practiceIdSchema, practiceMemberIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';
import { treatmentApproachesSchema } from '@nesso/common-contracts/treatment-approaches';

import { clientIdSchema, sessionIdSchema } from './ids';
import {
    inputMethodSchema,
    sessionActionsSchema,
    sessionLocationSchema,
    sessionStatusSchema,
} from './session.models';
import { formatSettingsSchema, sessionNoteTypeSchema } from './session-note.models';

export const SESSION_SCHEDULE_TYPE = 'session/schedule';
export const SESSION_UPDATE_TYPE = 'session/update';
export const SESSION_GENERATE_TYPE = 'session/generate';
export const SESSION_RESOLVE_ACTION_TYPE = 'session/resolve-action';
export const SESSION_DELETE_TYPE = 'session/delete';
export const SESSION_RELAY_OUTBOX_TYPE = 'session/relay-outbox';

export const sessionDelete = defineCommand(
    SESSION_DELETE_TYPE,
    z.object({
        id: sessionIdSchema,
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
);

const sessionBaseFields = {
    practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    date: z.iso.datetime(),
    duration: z.number().min(1).meta(metadata.nonSensitive),
    clientIds: z.array(clientIdSchema).min(1).meta(metadata.nonSensitive),
    treatmentApproaches: treatmentApproachesSchema.optional(),
    actions: sessionActionsSchema.optional().meta(metadata.nonSensitive),
    inputMethod: inputMethodSchema.nullable().meta(metadata.nonSensitive),
};

export const sessionSchedule = defineCommand(
    SESSION_SCHEDULE_TYPE,
    z.object({
        ...sessionBaseFields,
        clientIds: z.array(clientIdSchema).length(1).meta(metadata.nonSensitive),
        location: sessionLocationSchema,
        serviceCode: z.string(),
        feeCents: centsSchema,
    }),
    z.object({ id: z.uuid() }),
);

export const sessionRelayOutbox = defineCommand(SESSION_RELAY_OUTBOX_TYPE, z.object({}));

export const sessionUpdate = defineCommand(
    SESSION_UPDATE_TYPE,
    z.object({
        id: sessionIdSchema,
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        treatmentApproaches: treatmentApproachesSchema.min(1).optional(),
        status: sessionStatusSchema.optional().meta(metadata.nonSensitive),
    }),
);

export const sessionGenerate = defineCommand(
    SESSION_GENERATE_TYPE,
    z.object({
        ...sessionBaseFields,
        id: sessionIdSchema.meta(metadata.nonSensitive),
        noteType: sessionNoteTypeSchema.meta(metadata.nonSensitive),
        settings: formatSettingsSchema.optional().meta(metadata.nonSensitive),
    }),
    z.object({ id: z.uuid() }),
);

export const sessionResolveAction = defineCommand(
    SESSION_RESOLVE_ACTION_TYPE,
    z.object({
        id: sessionIdSchema,
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        action: sessionActionsSchema.unwrap(),
    }),
);
